import React, { useContext, useState } from 'react';

import { PaletteView } from 'src/modules/designStudio/components/DesignMenu/Menu/Templates/PaletteView';
import { TemplatesView } from 'src/modules/designStudio/components/DesignMenu/Menu/Templates/TemplatesView';
import { TEMPLATE_STYLES } from 'src/modules/designStudio/helpers/data/TemplateStyles';
import {
  MacheteBuildingContext,
  historyContext,
} from 'src/modules/designStudio/providers';
import { context } from 'src/modules/designStudio/providers/DesignStudioServiceProvider';
import { buttonPressed } from 'src/shared/telemetry';
import type { ButtonPressed } from 'src/shared/telemetry';

enum TemplateViewMode {
  VIEW_TEMPLATES,
  VIEW_PALETTES,
}

/**
 * @description This component does not directly manage the html show but rather manages the state and interactions between the pallete and templates view.
 * Interactions:
 * 1. When a user selects a palette in tem[plate view, it needs to be selected in pallete's view,
 * and vice versa.
 * 2. When a template is chnaged the pallete[] is chnaged, and the row and palettes view need to be updated
 *
 * @constructor
 */
export const TemplateMenu = ({ closeMenu }: { closeMenu: () => void }) => {
  const templates = TEMPLATE_STYLES;

  const { historyState } = useContext(historyContext);
  const { appService } = useContext(context);

  const { building } = useContext(MacheteBuildingContext);
  const { templateSelected } = useContext(historyContext);
  const [viewMode, setViewMode] = useState(TemplateViewMode.VIEW_TEMPLATES);

  const [selectedTemplateIndex, setSelectedTemplateIndex] = useState<
    number | undefined
  >(undefined);
  const [selectedPaletteIndex, setSelectedPaletteIndex] = useState<number>(0);
  const [isMiniPaletteRowVisible, setIsMiniPaletteRowVisible] =
    useState<boolean>(false);

  const palettes =
    selectedTemplateIndex === undefined
      ? []
      : templates[selectedTemplateIndex].palettes;

  const appSnapshot = appService.getSnapshot();

  const commonTrackingData = {
    primary_cta: false,
    job_id: historyState.jobId ?? undefined,
    org_id: appSnapshot.context.orgId,
    partner_id: appSnapshot.context.partnerId,
    user_id: appSnapshot.context.userId,
    user_email: historyState.email ?? undefined,
    page_or_screen_name: 'Design-studio',
    design_studio_mode: 'default',
    feature: 'design_templates',
    button_location: 'design-template-panel',
  } as ButtonPressed;

  const actions = {
    onMenuClosed: () => {
      closeMenu();
      buttonPressed({
        ...commonTrackingData,
        track_id: 'close-design-template-panel',
        button_text: '',
      });
    },
    onTemplateSelected: async (index: number) => {
      setSelectedTemplateIndex(index);
      setSelectedPaletteIndex(0);
      setIsMiniPaletteRowVisible(true);
      if (building) {
        await building.designer.set_active_design({
          ...templates[index].style,
          designName: 'user-custom-template',
        });
        templateSelected();
      }
      buttonPressed({
        ...commonTrackingData,
        track_id: 'design-template-design-selected',
        button_text: `${templates[index].style.designName ?? ''}`,
      });
    },
    onPaletteSelected: async (index: number) => {
      setSelectedPaletteIndex(index);
      if (building) {
        await building.designer
          .get_active_design()
          .apply_palette(palettes[index]);
        templateSelected();
      }
      buttonPressed({
        ...commonTrackingData,
        button_location:
          viewMode === TemplateViewMode.VIEW_TEMPLATES
            ? 'design-template-mini-color-palette-selected'
            : 'design-template-color-palette-scroll',
        track_id:
          viewMode === TemplateViewMode.VIEW_TEMPLATES
            ? 'design-template-mini-color-palette-selected'
            : 'design-template-full-color-palette-selected',
        button_text: `${palettes[index].paletteName ?? ''}`,
      });
    },
    onPaletteRowShowMore: () => {
      setViewMode(TemplateViewMode.VIEW_PALETTES);
      buttonPressed({
        ...commonTrackingData,
        track_id: 'additional-color-palettes',
        button_text: `${palettes.length - 3}`,
      });
    },
    onPaletteRowCancel: () => {
      setIsMiniPaletteRowVisible(false);
      buttonPressed({
        ...commonTrackingData,
        track_id: 'hide-mini-palette-row',
        button_text: '',
      });
    },
    onPaletteViewBack: () => {
      setViewMode(TemplateViewMode.VIEW_TEMPLATES);
    },
  };

  if (viewMode === TemplateViewMode.VIEW_TEMPLATES) {
    return (
      <TemplatesView
        closeMenu={actions.onMenuClosed}
        isMiniPaletteRowVisible={isMiniPaletteRowVisible}
        onPaletteRowCancel={actions.onPaletteRowCancel}
        onPaletteRowShowMore={actions.onPaletteRowShowMore}
        onPaletteSelected={actions.onPaletteSelected}
        onTemplateSelected={actions.onTemplateSelected}
        palettes={palettes}
        selectedPaletteIndex={selectedPaletteIndex}
        selectedTemplateIndex={selectedTemplateIndex}
        templates={templates}
      />
    );
  }
  if (viewMode === TemplateViewMode.VIEW_PALETTES) {
    return (
      <PaletteView
        closeMenu={actions.onMenuClosed}
        onPaletteSelected={actions.onPaletteSelected}
        onPaletteViewBack={actions.onPaletteViewBack}
        palettes={palettes}
        selectedPaletteIndex={selectedPaletteIndex}
      />
    );
  }
  return null;
};
